import {
  NAV_ARRANGEMENT,
  REGISTRY_BLOCK,
  THEME_ID,
  type NavArrangement,
  type ThemeId,
} from '../catalog/platformCatalogIds';
import { LAYOUT_COMPONENT_TYPE, type LayoutBlock } from '../content/studio';

/**
 * Nav chrome presentation modes — reused across hero registry blocks.
 *
 * Design contract: when we design a hero/nav pair in the IDE, add or update
 * `HERO_HARMONIZED_NAV_PRESENTATION` / `HERO_HARMONIZED_NAV_ARRANGEMENT` here.
 * Bootstrap seeds nav Look + Arrangement from these maps. Studio keeps Look
 * owner-controlled (Axes → Chrome); Arrangement may still sync when the hero changes.
 */
export const NAV_PRESENTATION = {
  classic: 'classic',
  /**
   * Cinematic light ink over photo heroes (fullbleed / video / reel).
   * Without those underlays, panel mode uses sticky theme-ink (`data-nav-ground`).
   */
  overlay: 'overlay',
  /** Floating frosted glass bar — theme ink on a controlled surface. */
  capsule: 'capsule',
  quiet: 'quiet',
  frosted: 'frosted',
  conversion: 'conversion',
  whisper: 'whisper',
  editorial: 'editorial',
} as const;

export const NAV_PRESENTATION_VALUES = [
  NAV_PRESENTATION.classic,
  NAV_PRESENTATION.overlay,
  NAV_PRESENTATION.capsule,
  NAV_PRESENTATION.quiet,
  NAV_PRESENTATION.frosted,
  NAV_PRESENTATION.conversion,
  NAV_PRESENTATION.whisper,
  NAV_PRESENTATION.editorial,
] as const;

export type NavPresentation = (typeof NAV_PRESENTATION_VALUES)[number];

export function isNavPresentation(value: string): value is NavPresentation {
  return (NAV_PRESENTATION_VALUES as readonly string[]).includes(value);
}

/**
 * Registry pairing: hero block key → harmonized nav presentation.
 * Authoritative UX contract — seeded into Navigation_Header at bootstrap.
 */
const P = REGISTRY_BLOCK.presentation;

export const HERO_HARMONIZED_NAV_PRESENTATION: Readonly<
  Record<string, NavPresentation>
> = {
  [P.heroEditorialSplit]: NAV_PRESENTATION.classic,
  /** Capsule glass stays readable on light About panels; Overlay remains an owner Look. */
  [P.heroFullBleedImage]: NAV_PRESENTATION.capsule,
  /** Reel fade — same Capsule glass over immersive photo stack as Full-Bleed. */
  [P.heroReelFade]: NAV_PRESENTATION.capsule,
  /** Broth steam — full-width frosted mist bar (not Tile Awning capsule pill). */
  [P.heroBrothSteam]: NAV_PRESENTATION.frosted,
  [P.heroVideoAtmosphere]: NAV_PRESENTATION.capsule,
  [P.heroStandard]: NAV_PRESENTATION.quiet,
  [P.heroCoffeeSpotlight]: NAV_PRESENTATION.frosted,
  [P.heroUtilityReservation]: NAV_PRESENTATION.conversion,
  [P.heroQuietLuxury]: NAV_PRESENTATION.whisper,
  /** Counter Bleed — whisper hairline over cinematic counter photo + seal. */
  [P.heroCounterBleed]: NAV_PRESENTATION.whisper,
  [P.heroCollageStory]: NAV_PRESENTATION.editorial,
};

/**
 * Registry pairing: hero block key → harmonized nav Arrangement.
 *
 * Compass (centered brand) for Monolith. Curtain for Quiet Luxury ceremony.
 * Ledger for Utility booking desk. Unlisted heroes default to Rail via
 * `resolveHeroHarmonizedNavArrangement`.
 */
export const HERO_HARMONIZED_NAV_ARRANGEMENT: Readonly<
  Partial<Record<string, NavArrangement>>
> = {
  [P.heroStandard]: NAV_ARRANGEMENT.compass,
  [P.heroQuietLuxury]: NAV_ARRANGEMENT.curtain,
  [P.heroCounterBleed]: NAV_ARRANGEMENT.curtain,
  [P.heroUtilityReservation]: NAV_ARRANGEMENT.ledger,
  [P.heroEditorialSplit]: NAV_ARRANGEMENT.curtain,
};

/** Theme seed fallback when page has no hero block — mirrors `THEME_BLOCK_PRESENTATIONS` nav picks. */
export const THEME_SUGGESTED_NAV_PRESENTATION: Partial<Record<ThemeId, NavPresentation>> = {
  [THEME_ID.michelinDark]: NAV_PRESENTATION.classic,
  /** Glasshouse mist bar over the specimen pedestal. */
  [THEME_ID.limestoneOrangery]: NAV_PRESENTATION.frosted,
  /** Estate grounds photography — Overlay chrome (not Tile capsule). */
  [THEME_ID.moorlandHearth]: NAV_PRESENTATION.overlay,
  [THEME_ID.bistroBrasserie]: NAV_PRESENTATION.editorial,
  /** Counter film — whisper over Counter Bleed (was Quiet Luxury / Monolith). */
  [THEME_ID.hinokiCounter]: NAV_PRESENTATION.whisper,
  /** Herb mist / broth paper — frosted sticky bar (distinct from Tile capsule). */
  [THEME_ID.lotusSteam]: NAV_PRESENTATION.frosted,
  /** Full-bleed awning facade — capsule glass over shopfront photography. */
  [THEME_ID.tileAwning]: NAV_PRESENTATION.capsule,
  /** Quiet-luxury courtyard — whisper hairline, not Tile capsule. */
  [THEME_ID.lanternCourt]: NAV_PRESENTATION.whisper,
  [THEME_ID.brutalistGrind]: NAV_PRESENTATION.frosted,
  [THEME_ID.earthClay]: NAV_PRESENTATION.capsule,
  [THEME_ID.neonNoir]: NAV_PRESENTATION.capsule,
  [THEME_ID.velvetVintage]: NAV_PRESENTATION.whisper,
  [THEME_ID.boldStreet]: NAV_PRESENTATION.conversion,
  /** Full-bleed stand facade — capsule over photography (Signal arrangement). */
  [THEME_ID.mustardRail]: NAV_PRESENTATION.capsule,
  /** Collage mezze story — editorial ink bar (Rail arrangement). */
  [THEME_ID.tahiniWindow]: NAV_PRESENTATION.editorial,
  [THEME_ID.cleanNomad]: NAV_PRESENTATION.capsule,
};

function isHeroBlock(block: LayoutBlock): boolean {
  if (
    block.componentType === LAYOUT_COMPONENT_TYPE.heroEditorialSplit ||
    block.componentType === LAYOUT_COMPONENT_TYPE.heroVideoAtmosphere
  ) {
    return true;
  }
  return block.registryBlockKey?.startsWith('Hero_') ?? false;
}

function isHeroRegistryKey(registryKey: string): boolean {
  return registryKey.startsWith('Hero_');
}

/** First hero key in a tier starter / allowed block list (registry order). */
export function resolveStarterHeroRegistryKey(
  starterBlocks: readonly string[],
): string | null {
  return starterBlocks.find(isHeroRegistryKey) ?? null;
}

/** First hero on the page (by display order) — fallback when nav block lacks seeded pairing. */
export function resolvePrimaryHeroRegistryKey(blocks: LayoutBlock[]): string | null {
  const ordered = [...blocks].sort((a, b) => a.displayOrder - b.displayOrder);
  const hero = ordered.find((b) => isHeroBlock(b));
  return hero?.registryBlockKey ?? null;
}

/** Paired Look chrome for a hero registry key (undefined when unknown). */
export function resolveHeroHarmonizedNavPresentation(
  heroRegistryKey?: string | null,
): NavPresentation | undefined {
  if (!heroRegistryKey) {
    return undefined;
  }
  return HERO_HARMONIZED_NAV_PRESENTATION[heroRegistryKey];
}

/**
 * Paired Arrangement for a hero registry key.
 * Defaults to Rail — only Monolith opts into Compass.
 */
export function resolveHeroHarmonizedNavArrangement(
  heroRegistryKey?: string | null,
): NavArrangement {
  if (!heroRegistryKey) {
    return NAV_ARRANGEMENT.rail;
  }
  return HERO_HARMONIZED_NAV_ARRANGEMENT[heroRegistryKey] ?? NAV_ARRANGEMENT.rail;
}

/**
 * Resolves nav chrome presentation — site-wide (same on every page).
 * Priority: block content (`navPresentation`) → registry hero pairing → theme → `classic`.
 *
 * Look is owner-configured on the nav block. Changing a page hero Arrangement no
 * longer overwrites chrome (Full Bleed must not force Overlay). Bootstrap still
 * seeds an initial pairing; studio Axes → Look is the ongoing control.
 */
export function resolveNavPresentation(
  themeId?: string,
  blockOverride?: string | null,
  heroRegistryKey?: string | null,
): NavPresentation {
  if (blockOverride && isNavPresentation(blockOverride)) {
    return blockOverride;
  }
  const fromHero = resolveHeroHarmonizedNavPresentation(heroRegistryKey);
  if (fromHero) {
    return fromHero;
  }
  const fromTheme = themeId
    ? THEME_SUGGESTED_NAV_PRESENTATION[themeId as ThemeId]
    : undefined;
  return fromTheme ?? NAV_PRESENTATION.classic;
}

/**
 * Out-of-flow nav sits on the hero — a logo mark reads cleaner over photography
 * than typography alone. Bootstrap may seed a default mark; brand title still
 * shows beside it when `siteTitle` has a value.
 */
export function navPresentationPrefersLogo(presentation: NavPresentation): boolean {
  return (
    presentation === NAV_PRESENTATION.overlay ||
    presentation === NAV_PRESENTATION.capsule ||
    presentation === NAV_PRESENTATION.frosted ||
    presentation === NAV_PRESENTATION.quiet ||
    presentation === NAV_PRESENTATION.whisper
  );
}

/** Reads optional `navPresentation` from block content (same value across locales). */
export function readNavPresentationBlockOverride(
  content: Record<string, Record<string, string>>,
): string | undefined {
  const field = content.navPresentation;
  if (!field) {
    return undefined;
  }
  return field.de ?? field.en ?? field.vi ?? Object.values(field)[0];
}

/** Registry keys whose media underlay can carry Overlay cinematic chrome. */
const OVERLAY_HERO_UNDERLAY_KEYS = new Set<string>([
  P.heroFullBleedImage,
  P.heroVideoAtmosphere,
  P.heroReelFade,
  P.heroCounterBleed,
]);

/**
 * True when this page’s blocks include a cinematic photo/video underlay for
 * Adaptive Overlay / Whisper / Quiet (hero ground) — Home heroes, Classical
 * Columns masthead, or Contact Threshold photo. About/legal (and menu/contact
 * without a photo plane) return false so chrome falls back to panel ground.
 * Studio scoped preview only mounts the active page canvas, so Home’s Full
 * Bleed does not leak onto Speisekarte / Kontakt.
 */
export function pageHasOverlayHeroUnderlay(blocks: LayoutBlock[]): boolean {
  return blocks.some((block) => {
    if (block.componentType === LAYOUT_COMPONENT_TYPE.heroVideoAtmosphere) {
      return true;
    }
    const key = block.registryBlockKey;
    if (key && OVERLAY_HERO_UNDERLAY_KEYS.has(key)) {
      return true;
    }
    // Menu masthead photo — same cinematic plane as Counter / Full Bleed.
    if (
      block.componentType === LAYOUT_COMPONENT_TYPE.menuGrid3Column &&
      typeof block.media?.menuImage === 'string' &&
      block.media.menuImage.length > 0
    ) {
      return true;
    }
    // Contact Threshold — photograph owns the first screen.
    if (
      key === P.contactThreshold &&
      typeof block.media?.mapImage === 'string' &&
      block.media.mapImage.length > 0
    ) {
      return true;
    }
    return false;
  });
}

/** `data-nav-ground` value for Adaptive Overlay. */
export function resolveNavGround(blocks: LayoutBlock[]): 'hero' | 'panel' {
  return pageHasOverlayHeroUnderlay(blocks) ? 'hero' : 'panel';
}
